import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Box, Center, Flex, Image, Stack, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
// reactstrap components
import { useDebounceFn } from "ahooks";

import { ThemedButton } from "@/components/ThemedButton";

import useLoginSession from "@/hooks/useLoginSession";

import avatarApi from "services/avatar-api";

import { getErrorMessage } from "utilities/error";

import backgroundLeftImage from "@/assets/img/Avatar/bgL.png";
import backgroundMidImage from "@/assets/img/Avatar/bgM.png";
import backgroundRightImage from "@/assets/img/Avatar/bgR.png";

import styles from "./AvatarPage.module.scss";
import { ImageSelectionCarousel, ImageSelectionGrid } from "./components/ImageSelections";

function AvatarScreen() {
	const { isLoginBySession, postSuccess, postErrorMessage } = useLoginSession();
	const [currentAvatarBackground, setCurrentAvatarBackground] = React.useState(null);
	const [currentAvatarOccupation, setCurrentAvatarOccupation] = React.useState(null);
	const [submitAvatarLoading, setSubmitAvatarsLoading] = useState(false);

	let navigate = useNavigate();

	const avatarsQuery = useQuery({
		cacheTime: 0,
		staleTime: 0,
		queryKey: ["avatars"],
		queryFn: async () => {
			const response = await avatarApi.getAvatars();
			return response.data;
		},
		onError: (error) => {
			toast.error(getErrorMessage(error));
		},
	});

	const avatarData = avatarsQuery.data;

	// Combine male and female options into single arrays
	const occupationOptions = useMemo(() => {
		const male = avatarData?.avatar_occupation_male || [];
		const female = avatarData?.avatar_occupation_female || [];
		return [...male, ...female].map((item) => ({
			id: item.id,
			src: item.avatar_occupation_selection_image,
			...item,
		}));
	}, [avatarData]);

	const backgroundOptions = useMemo(() => {
		const male = avatarData?.avatar_background_male || [];
		return [...male].map((item) => ({
			id: item.id,
			src: item.avatar_background_selection_image,
			...item,
		}));
	}, [avatarData]);

	useEffect(() => {
		if (avatarData && !currentAvatarOccupation && !currentAvatarBackground) {
			// default avatar
			if (avatarData.current_avatar) {
				// learner's avatar
				const avatarOccupation = {
					id: avatarData.current_avatar.avatar_occupation_id,
					src: avatarData.current_avatar.avatar_occupation_selection_image,
					avatar_occupation_gender: avatarData.current_avatar.avatar_occupation_gender,
					avatar_occupation_name: avatarData.current_avatar.avatar_occupation_name,
					avatar_occupation_description: avatarData.current_avatar.avatar_occupation_description,
					avatar_occupation_full_image: avatarData.current_avatar.avatar_occupation_full_image,
					avatar_occupation_selection_image:
						avatarData.current_avatar.avatar_occupation_selection_image,
				};
				const avatarBackground = {
					id: avatarData.current_avatar.avatar_background_id,
					src: avatarData.current_avatar.avatar_background_selection_image,
					avatar_background_gender: avatarData.current_avatar.avatar_background_gender,
					avatar_background_full_image: avatarData.current_avatar.avatar_background_full_image,
					avatar_background_selection_image:
						avatarData.current_avatar.avatar_background_selection_image,
				};
				setCurrentAvatarOccupation(avatarOccupation);
				setCurrentAvatarBackground(avatarBackground);
			} else {
				// Use first available option from combined arrays
				if (occupationOptions.length > 0) {
					setCurrentAvatarOccupation({
						id: occupationOptions[0].id,
						src: occupationOptions[0].src,
						...occupationOptions[0],
					});
				}
				if (backgroundOptions.length > 0) {
					setCurrentAvatarBackground({
						id: backgroundOptions[0].id,
						src: backgroundOptions[0].src,
						...backgroundOptions[0],
					});
				}
			}
		}
	}, [
		avatarData,
		occupationOptions,
		backgroundOptions,
		currentAvatarOccupation,
		currentAvatarBackground,
	]);

	const handleSubmitAvatar = useDebounceFn(
		(avatar_occupation_id, avatar_background_id) => {
			setSubmitAvatarsLoading(true);
			avatarApi.submitAvatar(avatar_occupation_id, avatar_background_id).then(
				() => {
					setSubmitAvatarsLoading(false);
					if (isLoginBySession) {
						avatarsQuery.refetch();
						postSuccess();
					} else {
						navigate(-1);
					}
					toast.success("Avatar Changed Successfully!");
				},
				(error) => {
					setSubmitAvatarsLoading(false);
					if (isLoginBySession) {
						postErrorMessage(getErrorMessage(error));
					} else {
						toast.error(getErrorMessage(error));
					}
				},
			);
		},
		{ leading: true, trailing: false, wait: 1000 },
	).run;

	return (
		<Box className={"fadeInDiv"}>
			<div className={`${styles.background_image_main_container}`}>
				<div className={`${styles.background_image_left_container}`}>
					<img
						className={styles.avatar_background_left_image}
						alt={"avatar_background_left"}
						src={backgroundLeftImage}
					/>
				</div>
				<div className={`${styles.background_image_mid_container}`}>
					<img
						className={styles.avatar_background_left_image}
						alt={"avatar_background_left"}
						src={backgroundMidImage}
					/>
				</div>
				<div className={`${styles.background_image_right_container}`}>
					<img
						className={styles.avatar_background_left_image}
						alt={"avatar_background_left"}
						src={backgroundRightImage}
					/>
				</div>
			</div>

			<Center>
				<Title order={2} py={20}>
					Avatar Creation
				</Title>
			</Center>

			<Box mx="auto" w={{ base: "100%", md: "80%" }} maw="1400px">
				{avatarsQuery.isLoading && (
					<div className="justify-content-center mt-4 w-100 flex">
						<span className="spinner-border spinner-border-sm"></span>
					</div>
				)}

				{!avatarsQuery.isLoading && !avatarsQuery.isError && (
					<>
						<Box>
							<Flex>
								<Flex
									visibleFrom="md"
									key={"occupation-list"}
									className={`${styles.selection_occupation_container}`}>
									<span className={`${styles.avatar_listing_selection_title_text}`}>
										Select Character
									</span>
									<ImageSelectionGrid
										data={occupationOptions}
										current={currentAvatarOccupation}
										onSelect={(item) => setCurrentAvatarOccupation(item)}
									/>
								</Flex>
								<Center flex={"1 1 auto"} pos="relative" w={{ base: "10%", md: "40%" }} mx="auto">
									<Image
										key={currentAvatarBackground?.id}
										h="100%"
										w="90%"
										fit="contain"
										className={"fadeInDiv"}
										src={currentAvatarBackground?.avatar_background_full_image}
										style={{
											backgroundSize: "contain",
											backgroundRepeat: "no-repeat",
											aspectRatio: 1,
										}}
									/>
									<Center h="100%" w="100%" top="0" left="0" pos="absolute">
										<Image
											key={currentAvatarOccupation?.id}
											className={"fadeInDiv"}
											mt="auto"
											h="90%"
											w="90%"
											style={{
												objectFit: "contain",
												aspectRatio: 1,
											}}
											fit="contain"
											alt={"display_occupation"}
											src={currentAvatarOccupation?.avatar_occupation_full_image}
										/>
									</Center>
								</Center>
								<Flex
									visibleFrom="md"
									key={"background-list"}
									className={`${styles.selection_background_container}`}>
									<span className={`${styles.avatar_listing_selection_title_text}`}>
										Select a Theme
									</span>
									<ImageSelectionGrid
										data={backgroundOptions.map((item) => ({
											id: item.id,
											src: item.avatar_background_selection_image,
											...item,
										}))}
										current={currentAvatarBackground}
										onSelect={(item) => setCurrentAvatarBackground(item)}
										size={{ base: 60, md: 80 }}
									/>
								</Flex>
							</Flex>

							<Stack hiddenFrom="md" pl={20} gap={30}>
								{Boolean(occupationOptions?.length) && (
									<Box>
										<Box className={`${styles.avatar_selection_title_text}`}>
											Select an Occupation
										</Box>
										<ImageSelectionCarousel
											current={currentAvatarOccupation}
											data={occupationOptions.map((item) => ({
												id: item.id,
												src: item.avatar_occupation_selection_image,
												...item,
											}))}
											onSelectAvatar={(item) => setCurrentAvatarOccupation(item)}
										/>
									</Box>
								)}
								{Boolean(backgroundOptions?.length) && (
									<Box>
										<span className={`${styles.avatar_selection_title_text}`}>Select a Theme</span>
										<ImageSelectionCarousel
											current={currentAvatarBackground}
											data={backgroundOptions.map((item) => ({
												id: item.id,
												src: item.avatar_background_selection_image,
												...item,
											}))}
											onSelectAvatar={(item) => setCurrentAvatarBackground(item)}
										/>
									</Box>
								)}
							</Stack>

							<div className="btn-container flex-wrap mt-4 mb-4 d-flex align-items-center justify-content-center">
								<ThemedButton
									px={50}
									loading={submitAvatarLoading}
									onClick={() =>
										handleSubmitAvatar(
											currentAvatarOccupation && currentAvatarOccupation.id
												? currentAvatarOccupation.id.toString()
												: null,
											currentAvatarBackground && currentAvatarBackground.id
												? currentAvatarBackground.id.toString()
												: null,
										)
									}>
									<span>Save </span>
								</ThemedButton>
							</div>
						</Box>
					</>
				)}

				{!avatarsQuery.isLoading && avatarsQuery.isError && (
					<div className="justify-content-center mt-4 w-100 flex">
						<p className="text-center flex">
							<b>{"Avatar Creation failed to load, Please Refresh the page or try again later!"}</b>
						</p>
					</div>
				)}
			</Box>
		</Box>
	);
}

// const FadeIn = ({ children }: { children: React.ReactElement }) => {
// 	const [animated, setAnimated] = useState(false);
// 	return React.cloneElement(children, {
// 		className: `${children.props.className} fadeInDiv`,
// 	});
// };

export default AvatarScreen;
